import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import InterviewPermission from "./components/InterviewPermission/InterviewPermission";
import InterviewProvider from "./context/InterviewProvider";
import InterviewMediaRecorderControls from "./components/InterviewVideoChatPlatform/InterviewMediaRecorderControls/InterviewMediaRecorderControls";
import InterviewEndPlatform from "./components/InterviewVideoChatPlatform/InterviewEndPlatform/InterviewEndPlatform";
import { addPreAssessmentData } from "@workspace/job/src/store/preAssessment/action";
import { toast } from "react-toastify";
import { Modal, ModalBody, Button } from "reactstrap";
import InterviewAttemptFailed from "./components/InterviewAttemptFailed.jsx/InterviewAttemptFailed";
import LoadingOverlay from "react-loading-overlay";
import InterviewVideoChatPlatform from "./components/InterviewVideoChatPlatform/InterviewVideoChatPlatform";
import InterviewVideoAIPlatformRealtime from "./components/InterviewVideoChatPlatform/InterviewVideoAIPlatform/InterviewVideoAIPlatformRealtime";
import { v4 as uuidv4 } from "uuid";

const InteractWithAI = ({ jobData }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("q");

  const [step, setStep] = useState(0);
  const [permissions, setPermissions] = useState({
    micPermission: false,
    cameraPermission: false,
  });
  const [recordedVideoBlobUrl, setRecordedVideoBlobUrl] = useState(null);
  const [interviewQuestions, setInterviewQuestions] = useState(null);
  const [
    isInterviewAttemptFailedModalOpen,
    setIsInterviewAttemptFailedModalOpen,
  ] = useState(false);
  const preAssessmentToken = useSelector(
    (state) => state.PreAssessmentReducer.preAssessmentToken
  );
  const [isLoadingOverlay, setIsLoadingOverlay] = useState(false);
  const uploadIsLoading = useSelector(
    (state) => state.PreAssessmentReducer.loading
  );
  const [language, setLanguage] = useState({
    label: "English",
    value: "english",
  });
  const [completeInterviewQnsArray, setCompleteInterviewQnsArray] = useState(
    []
  );

  useEffect(() => {
    if (
      jobData &&
      Object.keys(jobData).length > 0 &&
      jobData?.preAssessmentData?.questions &&
      jobData?.preAssessmentData?.questions?.length > 0
    ) {
      if (
        jobData?.preAssessmentData &&
        jobData?.preAssessmentData?.questions &&
        jobData?.preAssessmentData?.questions?.length === 0
      ) {
        toast.error("There are no interview questions available");
        return;
      }
      setCompleteInterviewQnsArray(jobData?.preAssessmentData?.questions);
    }
  }, [jobData]);

  // Function to shuffle and pick random interview questions from the list
  const randomizeAndPickInterviewQuestions = (interviewQuestions, number) => {
    if (!interviewQuestions) return [];
    if (interviewQuestions.length === 0) return [];
    if (interviewQuestions?.length <= number || number === 0)
      return interviewQuestions;

    const shuffled = [...interviewQuestions];
    const n = Math.min(number, interviewQuestions.length);

    // Perform the shuffle
    for (let i = shuffled.length - 1; i > shuffled.length - 1 - n; i--) {
      const randomIndex = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[randomIndex]] = [
        shuffled[randomIndex],
        shuffled[i],
      ];
    }

    // Return the first `count` items of the shuffled array
    return shuffled.slice(-n);
  };

  const generateInterviewQuestionString = (interviewQuestions) => {
    if (!interviewQuestions) return "";
    if (interviewQuestions.length === 0) return "";
    let questionString = "";
    interviewQuestions?.forEach((question, index) => {
      questionString += `${index + 1}. ${question}\n`;
    });
    return questionString;
  };

  const convertURLToFile = async (mediaBlobUrl) => {
    try {
      // Fetch the blob from the mediaBlobUrl
      const response = await fetch(mediaBlobUrl);
      const blob = await response.blob();

      // Convert the Blob to a File object
      const videoFile = new File([blob], "recordedVideo.webm", {
        type: "video/webm",
      });
      return videoFile;
    } catch (error) {
      toast.error("Failed to convert URL to file");
    }
  };

  const handleCompleteInterview = async (
    convertVideoURL,
    chatHistory,
    timeStamp,
    expiredLinkOnCompleted = true
  ) => {
    if (!token) {
      toast.error("invalid link");
      return;
    }
    // Convert URL to blob
    let videoFile = null;
    if (convertVideoURL) {
      videoFile = await convertURLToFile(convertVideoURL);
    }
    const formData = new FormData();
    formData.append("video", videoFile);
    formData.append("chatHistory", JSON.stringify(chatHistory));
    formData.append("timeStamp", JSON.stringify(timeStamp));
    formData.append("token", token);
    formData.append("expiredLinkOnCompleted", expiredLinkOnCompleted);

    // Set the questions
    if (
      jobData &&
      Object.keys(jobData).length > 0 &&
      jobData?.preAssessmentData?.questions &&
      jobData?.preAssessmentData?.questions?.length > 0
    ) {
      formData.append(
        "questions",
        JSON.stringify(jobData?.preAssessmentData?.questions)
      );
    }

    // Handle the data here
    dispatch(addPreAssessmentData(formData));
    return;
  };

  // Randomly select 2 questions for the user if the lanaguage is not english
  useEffect(() => {
    const interviewQnsArray = completeInterviewQnsArray.map(
      (question) => question?.question
    );
    setInterviewQuestions(generateInterviewQuestionString(interviewQnsArray));

    if (language?.value === "others") {
      //  Shuffle and pick 2 random questions
      const randomInterviewQuestions = randomizeAndPickInterviewQuestions(
        completeInterviewQnsArray,
        2
      );
      const interviewQnsArray = randomInterviewQuestions.map(
        (question) => question?.question
      );
      setInterviewQuestions(generateInterviewQuestionString(interviewQnsArray));
    }
  }, [language, completeInterviewQnsArray]);

  const generateContentByStep = () => {
    switch (step) {
      case 0:
        return (
          <InterviewPermission
            permissions={permissions}
            setPermissions={setPermissions}
            setStep={setStep}
            setLanguage={setLanguage}
            language={language}
          />
        );
      case 1:
        return (
          <InterviewProvider>
            <InterviewVideoChatPlatform
              jobId={jobData?.id}
              onCompleteInterview={handleCompleteInterview}
              step={step}
              setStep={setStep}
              videoRecorderComponent={
                <InterviewMediaRecorderControls
                  setIsLoadingOverlay={setIsLoadingOverlay}
                  recordedVideoBlobUrlValue={recordedVideoBlobUrl}
                  onUpload={(recordedBlobURL) => {
                    setRecordedVideoBlobUrl(recordedBlobURL);
                    onUpload(recordedBlobURL, setFieldValue);
                    toast.success("Video uploaded successfully");
                  }}
                  onRemoveUpload={() => {
                    setFieldValue("recordedVideo", null);
                    setRecordedVideoBlobUrl(null);
                  }}
                />
              }
              interviewAIComponent={
                // <InterviewVideoAIPlatform
                <InterviewVideoAIPlatformRealtime
                  language={language}
                  token={token}
                  setIsInterviewAttemptFailedModalOpen={
                    setIsInterviewAttemptFailedModalOpen
                  }
                  setStep={setStep}
                  interviewQuestions={interviewQuestions}
                  onCompleteInterview={(
                    interviewChatHistory,
                    interviewTimeStamp
                  ) =>
                    handleCompleteInterview(
                      interviewChatHistory,
                      interviewTimeStamp
                    )
                  }
                />
              }
              interviewEndComponent={
                <InterviewEndPlatform
                  onCompleteInterview={handleCompleteInterview}
                />
              }
            />
          </InterviewProvider>
        );
      default:
        return null;
    }
  };

  const generateLoadingText = () => {
    if (isLoadingOverlay) {
      return "Converting video...please wait";
    }
    if (uploadIsLoading) {
      return (
        <div style={{ color: "white", margin: "0", width: "400px" }}>
          <p>Uploading self assessment data. Please Wait...</p>
          <p>
            Your interview recording is being uploaded. This process may take
            2-3 minutes, and the window will close automatically once the upload
            is complete.
          </p>
          <p>
            Please do not close the window during this time, as it may interrupt
            the upload and prevent the video from being saved. Thank you for
            your patience!
          </p>
        </div>
      );
    }
  };

  return (
    <LoadingOverlay
      active={isLoadingOverlay || uploadIsLoading || false}
      spinner
      text={generateLoadingText()}
    >
      <div>
        {generateContentByStep()}
        <Modal
          isOpen={isInterviewAttemptFailedModalOpen}
          centered
          backdropClassName="modal"
          style={{
            width: "450px",
          }}
        >
          <ModalBody>
            <div className="text-center mb-3">
              <span className="fs-2 fw-semibold">
                Last Attempt was a Failure
              </span>
            </div>
            <div className="text-center mb-3">
              <span
                className="fs-4"
                style={{
                  color: "#6C757D",
                }}
              >
                The last attempt failed due to a technical issue. Please join
                using the link or button provided below.
              </span>
            </div>
            <InterviewAttemptFailed
              linkUrl={`${window.location.origin}/public?rq=${preAssessmentToken?.token}&redirect=true`}
            />
            <a
              href={`${window.location.origin}/public?rq=${preAssessmentToken?.token}&redirect=true`}
            >
              <Button
                className="mt-3 d-flex align-items-center gap-2 justify-content-center"
                style={{
                  backgroundColor: "#0063B5",
                  color: "#FFFFFF",
                  width: "100%",
                }}
              >
                <span>Join Again </span>
                <i className=" ri-arrow-right-line fs-5"></i>
              </Button>
            </a>
          </ModalBody>
        </Modal>
      </div>
    </LoadingOverlay>
  );
};

export default InteractWithAI;
